import React, { useRef, useState } from 'react';
import { useI18nContext } from "../../../contexts/I18nContext";
import RoundedButton from "../../../ui_components/RoundedButton/RoundedButton";
import { Observer } from "mobx-react-lite";
import { useStyles } from "./NewAttachmentButton.style";
import Button from "../../../ui_components/Button/Button";
import ButtonType from "../../../ui_components/Button/ButtonType";
import { useCommonContext } from "../../../contexts/CommonContext";
import ButtonSize from "../../../ui_components/Button/ButtonSize";
import NewAttachmentButtonProperties from './NewAttachmentButtonProperties';
import Modal from "../../../ui_components/Modal/Modal";
import ImageIcon from '@mui/icons-material/Image';
import Link from '../../../ui_components/Link/Link';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TextInput from "../../../ui_components/TextInput/TextInput";
import NewAttachmentStore from './NewAttachmentStore';
import ProductOrderAttachmentType, { translateProductOrderAttachmentType } from '../../../model/productOrder/ProductOrderAttachmentType';

const NewAttachmentButton: React.FC<NewAttachmentButtonProperties> = ({
  showIcon = true,
  showText = true,
  disabled,
  className = '',
  productOrderId,
  rounded = false,
  isLinkText,
  size = ButtonSize.DEFAULT,
  type = ButtonType.PRIMARY,
  onClose,
  onUploadSuccess,
}) => {
  const classes = useStyles();
  const i18n = useI18nContext();
  const commonContext = useCommonContext();
  const store = new NewAttachmentStore(commonContext, productOrderId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  let uploadSuccess = false;

  const handleOnclick = async () => {
    await store.openModal();
  };

  const onCloseModal = () => {
    store.closeModal();
    if (uploadSuccess && onClose) {
      onClose();
    }
    if (uploadSuccess && onUploadSuccess) {
      onUploadSuccess();
    }
    uploadSuccess = false;
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const success = await store.processSelectedImage(file);
      if (success && fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadAreaClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      const success = await store.processSelectedImage(file);
      if (success && fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    store.setImageDescription(value);
  };

  const handleTypeChange = (type: ProductOrderAttachmentType) => {
    store.setImageType(type);
  };

  const handleSave = async () => {
    const saved = await store.save();
    if (saved) {
      uploadSuccess = true;
      store.closeModal();
      if (onClose) {
        onClose();
      }
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    }
  };

  const text = "Nuevo anexo";
  const Icon = ImageIcon;

  const Btn = () => {
    if (isLinkText) {
      return (
        <Link
          text={text}
          onClick={handleOnclick}
          startIcon={ImageIcon}
        />
      );
    }
    if (rounded) {
      return (
        <RoundedButton
          onClick={handleOnclick}
          className={className}
          disabled={disabled}
          tooltip={!showText ? text : ""}
          text={showText ? text : ""}
          type={type}
          icon={showIcon ? <Icon /> : <></>}
        />
      );
    }
    return (
      <Button
        text={showText ? text : ""}
        className={className}
        tooltip={!showText ? text : ""}
        disabled={disabled}
        size={size}
        onClick={handleOnclick}
        type={type}
        startIcon={showIcon ? <Icon /> : <></>}
      />
    );
  };

  return (
    <Observer>
      {() => (
        <>
          <Modal
            fullScreen={false}
            loading={store.loading}
            title={text}
            onClose={onCloseModal}
            open={store.isModalOpen}
          >
            <div className={classes.modal_container}>
              <div className={classes.content_container}>
                {!store.selectedImageBase64 ? (
                  <div
                    className={`${classes.upload_area} ${dragOver ? 'drag-over' : ''}`}
                    onClick={handleUploadAreaClick}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <CloudUploadIcon className={classes.upload_icon} />
                    <div className={classes.upload_text}>
                      Haz clic para subir o arrastra un archivo aquí
                    </div>
                    <div className={classes.upload_subtext}>
                      Formatos compatibles: JPG, PNG
                    </div>
                  </div>
                ) : (
                  <div className={classes.preview_section}>
                    <img
                      src={store.selectedImagePreview}
                      alt="Preview"
                      className={classes.preview_image}
                    />
                    <button
                      onClick={handleUploadAreaClick}
                      className={classes.btn}
                    >
                      Cambiar
                    </button>
                    <div className={classes.preview_info}>
                      Imagen seleccionada. Puedes cambiarla haciendo clic en el botón "Cambiar".
                    </div>
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className={classes.hidden_input}
                />

                {store.selectedImageBase64 && (
                  <>
                    <div className={classes.type_form_group}>
                      <div className={classes.type_label}>Tipo de imagen</div>
                      <div className={classes.type_toggle}>
                        <button
                          type="button"
                          className={`${classes.type_option} ${store.imageType === ProductOrderAttachmentType.DELIVERY_EVIDENCE ? 'active' : ''}`}
                          onClick={() => handleTypeChange(ProductOrderAttachmentType.DELIVERY_EVIDENCE)}
                          disabled={store.isUploading}
                        >
                          <CheckCircleOutlineIcon fontSize="small" />
                          {translateProductOrderAttachmentType(ProductOrderAttachmentType.DELIVERY_EVIDENCE)}
                        </button>
                        <button
                          type="button"
                          className={`${classes.type_option} ${store.imageType === ProductOrderAttachmentType.DELIVERY_ISSUE ? 'active' : ''}`}
                          onClick={() => handleTypeChange(ProductOrderAttachmentType.DELIVERY_ISSUE)}
                          disabled={store.isUploading}
                        >
                          <ErrorOutlineIcon fontSize="small" />
                          {translateProductOrderAttachmentType(ProductOrderAttachmentType.DELIVERY_ISSUE)}
                        </button>
                      </div>
                    </div>
                    <TextInput
                      fullWidth
                      label={i18n.translate("DESCRIPTION")}
                      value={store.imageDescription}
                      onChange={handleDescriptionChange}
                      className={classes.description_input}
                      placeholder={'Describe la imagen (opcional)'}
                    />
                  </>
                )}

                <div className={classes.button_container}>
                  <Button
                    loading={store.isUploading}
                    className={classes.button}
                    onClick={handleSave}
                    type={ButtonType.SECONDARY}
                    text={i18n.translate("SAVE")}
                    disabled={!store.enableButton}
                  />
                  <Button
                    loading={store.isUploading}
                    className={classes.cancel_btn}
                    onClick={onCloseModal}
                    type={ButtonType.DEFAULT}
                    text={i18n.translate("CANCEL")}
                  />
                </div>
              </div>
            </div>
          </Modal>
          <Btn />
        </>
      )}
    </Observer>
  );
};

export default NewAttachmentButton;
